/**
 * ExpressionCache — LRU cache for expression classification results.
 *
 * Stores *classification metadata* (type tag, optional literal value) rather than
 * evaluated results, because results depend on runtime state that changes between
 * evaluations. The classification itself is deterministic for a given expression
 * string and can be safely cached.
 */

export interface CachedExpression {
  type: string
  value?: unknown
  [key: string]: unknown
}

export interface CacheStats {
  size: number
  hits: number
  misses: number
  hitRate: number
}

export class ExpressionCache {
  private map: Map<string, CachedExpression>
  private maxSize: number
  private hits = 0
  private misses = 0

  constructor(maxSize = 1000) {
    this.maxSize = maxSize
    this.map = new Map()
  }

  get(key: string): CachedExpression | undefined {
    const entry = this.map.get(key)
    if (entry === undefined) {
      this.misses++
      return undefined
    }
    // Refresh position: delete + re-insert moves to end (most recent)
    this.map.delete(key)
    this.map.set(key, entry)
    this.hits++
    return entry
  }

  set(key: string, value: CachedExpression): void {
    // If key already exists, delete first so re-insert moves to end
    if (this.map.has(key)) {
      this.map.delete(key)
    } else if (this.map.size >= this.maxSize) {
      // Evict the oldest entry (first key in insertion order)
      const oldest = this.map.keys().next().value
      if (oldest !== undefined) {
        this.map.delete(oldest)
      }
    }
    this.map.set(key, value)
  }

  getStats(): CacheStats {
    const total = this.hits + this.misses
    return {
      size: this.map.size,
      hits: this.hits,
      misses: this.misses,
      hitRate: total === 0 ? 0 : this.hits / total,
    }
  }

  clear(): void {
    this.map.clear()
    this.hits = 0
    this.misses = 0
  }
}
